import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  DELIVERY_MODEL,
  REGIONS,
  formatAud,
  URBAN_DEVELOPMENT_PER_LOT_CENTS,
  CONSULTANCY_FROM_CENTS,
  GUIDED_REPORT_FROM_CENTS,
  DIY_TOOLS_MAX_CENTS,
  type ServiceRegion,
} from "@shared/types";
import { standardEaMiningEnquiryPath } from "@shared/standardEaMining";
import { AUDIENCES } from "../data/audiences";
import { GALLERY_ORDER, img, SITE_IMAGES } from "../data/media";
import { BrandLogo } from "../components/BrandLogo";
import { AudienceIcon, PatternBand, TierVisual } from "../components/Visuals";

export function HomePage() {
  const location = useLocation();
  const hero = img("rural-windmill");
  const heroSecondary = img("infra-chamber");

  useEffect(() => {
    if (location.hash === "#audiences") {
      document.getElementById("audiences")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  return (
    <>
      <section className="hero hero-visual hero-with-photo">
        <div className="hero-photo-bg" aria-hidden="true">
          <img src={hero.src} alt="" />
          <div className="hero-photo-scrim" />
        </div>
        <div className="container hero-grid">
          <div>
            <div className="eyebrow eyebrow-on-photo">
              Ecological Engineering · environmental and field programmes
            </div>
            <h1>Stop waiting months for engineering and environmental work you could finish this week.</h1>
            <p className="lead">
              Environmental and field programmes for site approvals and resource projects in
              Australia. Download proven tools now, or get a team-reviewed report in about 48 hours.
              Full consultancy stays available for complex risk, from{" "}
              {formatAud(CONSULTANCY_FROM_CENTS)} ex GST.
            </p>
            <div className="hero-actions">
              <Link className="btn btn-primary" to="/resources">
                Get tools now
              </Link>
              <Link className="btn btn-secondary" to="/reports">
                Get a report in ~48h
              </Link>
              <Link className="btn btn-secondary" to="/consulting">
                Consulting
              </Link>
            </div>
            <div className="hero-meta">
              <span>Ecological Engineering</span>
              <span>EcoEng.net</span>
              <span>AUD · GST invoices</span>
            </div>
          </div>
          <div className="hero-visual-frame hero-photo-stack">
            <figure className="hero-photo-main">
              <img src={heroSecondary.src} alt={heroSecondary.alt} />
              <figcaption>{heroSecondary.caption}</figcaption>
            </figure>
            <div className="hero-float-card">
              <BrandLogo variant="lockup" className="hero-float-logo" />
              <strong>Three ways to get unstuck</strong>
              <span>
                Free/paid tools · Guided from {formatAud(GUIDED_REPORT_FROM_CENTS)} · Consultancy
                from {formatAud(CONSULTANCY_FROM_CENTS)}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="consulting-strip" aria-label="Consulting practice">
        <div className="container consulting-strip-inner">
          <div>
            <p className="eyebrow" style={{ marginBottom: "0.35rem" }}>
              Practice
            </p>
            <p>
              Ecological Engineering delivers environmental and field programmes for approvals and
              resource projects in Australia. Tools and guided reports stay available when a
              download is the right start.
            </p>
          </div>
          <Link className="btn btn-primary" to="/consulting">
            Consulting
          </Link>
        </div>
      </section>

      <section className="section" id="audiences">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Who this is for</h2>
              <p>
                Developers, operators, agencies, and site teams. Start with a tool or a guided
                report. Use consulting when programme risk is real.
              </p>
            </div>
          </div>

          <div className="who-grid">
            {AUDIENCES.map((a) => (
              <article key={a.id} className="card who-card">
                <AudienceIcon id={a.id} className="who-card-icon" />
                <h3>{a.name}</h3>
                <p className="who-card-pain">{a.short}</p>
                <p className="who-card-outcome">{a.startWith}</p>
                <div className="who-card-actions">
                  <Link className="btn btn-primary btn-sm" to={a.ctaTo}>
                    {a.ctaLabel}
                  </Link>
                  <Link className="btn btn-outline btn-sm" to={`/contact?audience=${a.id}`}>
                    Ask a specialist
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="where-we-work" aria-label="Where we work">
            <span className="where-we-work-label">Where we work</span>
            {(Object.keys(REGIONS) as ServiceRegion[]).map((code) => {
              const r = REGIONS[code];
              return (
                <Link key={code} to={r.path} className="home-tier-chip">
                  {r.name} services
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <PatternBand />

      <section className="section section-alt" id="how-we-work">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Get what you need — without overbuying</h2>
              <p>
                Most projects should start with a download or a guided report. Full consultancy is
                for when the risk is real.
              </p>
            </div>
            <Link className="btn btn-outline" to="/pricing">
              See all pricing
            </Link>
          </div>
          <div className="grid-3">
            <article className="card card-hover tier-card tier-card-preferred">
              <TierVisual tier={1} />
              <span className="badge badge-free">Instant · DIY</span>
              <h3>Download the tool</h3>
              <p>
                Stuck without a checklist, canvas or field sheet? Grab free and paid PDFs (ASS,
                ESC, soils, marine survey, blue carbon) or buy Plant species ID from 3 photos.
              </p>
              <p className="price" style={{ fontSize: "1rem" }}>
                Free – {formatAud(DIY_TOOLS_MAX_CENTS)} <small>incl. GST</small>
              </p>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Link className="btn btn-primary btn-sm" to="/resources">
                  Open the library
                </Link>
                <Link className="btn btn-outline btn-sm" to="/resources/plant-species-id">
                  Plant species ID
                </Link>
              </div>
            </article>
            <article className="card card-hover tier-card tier-card-preferred">
              <TierVisual tier={2} />
              <span className="badge badge-paid">~48 hours · Guided</span>
              <h3>Get a reviewed report</h3>
              <p>
                Need a professional deliverable without a long consultancy cycle? Answer a smart
                questionnaire, upload files and logo — we review and release a co-branded PDF.
              </p>
              <p className="price" style={{ fontSize: "1rem" }}>
                From {formatAud(GUIDED_REPORT_FROM_CENTS)} <small>ex GST</small>
              </p>
              <Link className="btn btn-primary btn-sm" to="/reports">
                Start a guided report
              </Link>
            </article>
            <article className="card card-hover tier-card">
              <TierVisual tier={3} />
              <span className="badge badge-status">Scoped · Consultancy</span>
              <h3>Bring us onto the project</h3>
              <p>
                Multi-agency, high-risk or multi-site work that needs ongoing professional judgment.
                We’ll tell you if a tool or guided report is enough instead.
              </p>
              <p className="price" style={{ fontSize: "1rem" }}>
                From {formatAud(CONSULTANCY_FROM_CENTS)} <small>ex GST</small>
              </p>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Link className="btn btn-outline btn-sm" to="/contact">
                  Request a proposal
                </Link>
                <Link className="btn btn-primary btn-sm" to={standardEaMiningEnquiryPath()}>
                  Standard EA mining
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="section" id="practice-gallery">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Real sites. Real constraints.</h2>
              <p>
                From construction controls to coastal interfaces — this is the world our tools and
                reports are built for.
              </p>
            </div>
          </div>
          <div className="photo-gallery">
            {GALLERY_ORDER.map((id) => {
              const photo = SITE_IMAGES[id];
              return (
                <figure key={id} className="photo-gallery-item">
                  <img src={photo.src} alt={photo.alt} loading="lazy" />
                  <figcaption>
                    <strong>{photo.caption}</strong>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section section-alt" id="services">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Services by market</h2>
              <p>
                Pick your region. Every offering ships three ways — DIY tools, DIWY guided packs, or
                DIFY full delivery.
              </p>
            </div>
          </div>

          <div className="home-tier-row" aria-label="How engagement works">
            {(Object.keys(DELIVERY_MODEL) as Array<keyof typeof DELIVERY_MODEL>).map((key) => {
              const t = DELIVERY_MODEL[key];
              return (
                <Link key={key} to={t.path} className="home-tier-chip">
                  <span className="tier-code">{t.code}</span>
                  <span>{t.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="region-pick-grid">
            {(Object.keys(REGIONS) as ServiceRegion[]).map((code) => {
              const r = REGIONS[code];
              return (
                <Link key={code} to={r.path} className="region-pick-card">
                  <div className="region-pick-flag">{code.toUpperCase()}</div>
                  <h3>{r.name}</h3>
                  <p>{r.tagline}</p>
                  <ul className="region-focus-list">
                    {r.focus.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <span className="btn btn-primary btn-sm">Browse {r.name} services</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container split-visual">
          <div className="split-visual-art split-visual-photo">
            <img src={img("biomimicry-mural").src} alt={img("biomimicry-mural").alt} />
            <div className="split-stat-stack">
              <div className="split-stat">
                <strong>~48h</strong>
                <span>Typical guided turnaround</span>
              </div>
              <div className="split-stat">
                <strong>{formatAud(GUIDED_REPORT_FROM_CENTS)}</strong>
                <span>Guided reports from (ex GST)</span>
              </div>
              <div className="split-stat">
                <strong>{formatAud(CONSULTANCY_FROM_CENTS)}</strong>
                <span>Consultancy from (ex GST)</span>
              </div>
            </div>
          </div>
          <div>
            <h2>Don’t pay consultancy rates for a checklist problem.</h2>
            <p className="muted">
              If you need a template, buy the template. If you need a standard report, run a guided
              pack. Save full consultancy (from {formatAud(CONSULTANCY_FROM_CENTS)} ex GST) for
              complex pathways — including urban packages from{" "}
              {formatAud(URBAN_DEVELOPMENT_PER_LOT_CENTS)} per lot when that scope applies.
            </p>
            <ul className="check-list">
              <li>Need it today → DIY library</li>
              <li>Need a professional PDF this week → guided report</li>
              <li>Need ongoing specialist judgment → consultancy enquiry</li>
            </ul>
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "1.25rem" }}>
              <Link className="btn btn-primary" to="/resources">
                Browse tools
              </Link>
              <Link className="btn btn-outline" to="/reports">
                Guided reports
              </Link>
              <Link className="btn btn-outline" to="/pricing">
                Compare pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="container cta-band-inner">
          <div>
            <BrandLogo variant="footer" className="cta-band-logo" />
            <h2>Not sure where to start?</h2>
            <p>
              Tell us the site and the deadline. We’ll usually point you to a tool or guided report
              first — and only recommend consultancy when you truly need it.
            </p>
          </div>
          <div className="cta-band-actions">
            <Link className="btn btn-primary" to="/resources">
              Start with free tools
            </Link>
            <Link className="btn btn-secondary" to="/reports">
              Guided reports
            </Link>
            <Link className="btn btn-secondary" to="/contact">
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
